import { getCsrfToken } from "@/integrations/php/client";
import type { ServiceType } from "@/context/LeadContext";

export type PartnerAttentionReason =
  | "missing_services"
  | "missing_coordinates"
  | "missing_radius"
  | "missing_notification_channel";

export type PartnerMiniPageMissingField =
  | "image"
  | "description"
  | "phone"
  | "location"
  | "services";

export interface AdminPartnerDirectoryRow {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
  mini_image_url: string | null;
  mini_description: string | null;
  mini_page_complete: boolean;
  mini_page_missing: PartnerMiniPageMissingField[];
  services: ServiceType[];
  active: boolean;
  latitude: number | null;
  longitude: number | null;
  radius: number | null;
  sms_opt_out: boolean;
  created_at: string | null;
  total_leads: number;
  leads_30d: number;
  success_leads: number;
  last_lead: string | null;
  routing_ready: boolean;
  email_ready: boolean;
  sms_ready: boolean;
  attention_reasons: PartnerAttentionReason[];
}

export interface AdminPartnerDirectorySummary {
  total: number;
  active: number;
  inactive: number;
  routing_ready: number;
  needs_attention: number;
  geocoded: number;
  email_ready: number;
  sms_ready: number;
  quiet_30d: number;
  new_30d: number;
  total_leads: number;
  leads_30d: number;
  mini_page_complete: number;
  mini_page_incomplete: number;
  mini_page_missing_image: number;
  mini_page_missing_description: number;
}

export interface AdminPartnerServiceCoverage {
  service: ServiceType;
  active_partners: number;
}

export interface AdminPartnerDirectory {
  partners: AdminPartnerDirectoryRow[];
  summary: AdminPartnerDirectorySummary;
  service_coverage: AdminPartnerServiceCoverage[];
  generated_at: string;
}

export const EMPTY_PARTNER_DIRECTORY_SUMMARY: AdminPartnerDirectorySummary = {
  total: 0,
  active: 0,
  inactive: 0,
  routing_ready: 0,
  needs_attention: 0,
  geocoded: 0,
  email_ready: 0,
  sms_ready: 0,
  quiet_30d: 0,
  new_30d: 0,
  total_leads: 0,
  leads_30d: 0,
  mini_page_complete: 0,
  mini_page_incomplete: 0,
  mini_page_missing_image: 0,
  mini_page_missing_description: 0,
};

const toNumber = (value: unknown): number => Number(value || 0);
const nullableNumber = (value: unknown): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);
const nullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

export async function fetchAdminPartnerDirectory(): Promise<AdminPartnerDirectory> {
  const response = await fetch("/api/admin/partners.php", {
    credentials: "include",
    headers: { "X-CSRF-Token": await getCsrfToken() },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.error?.message || "Δεν φορτώθηκε το δίκτυο συνεργατών.");
  }

  const data = body?.data || {};
  const summary = data.summary || {};
  return {
    partners: Array.isArray(data.partners)
      ? data.partners.map((row: Record<string, unknown>) => ({
          id: String(row.id || ""),
          name: String(row.name || ""),
          phone: String(row.phone || ""),
          email: String(row.email || ""),
          location: String(row.location || ""),
          mini_image_url: nullableString(row.mini_image_url),
          mini_description: nullableString(row.mini_description),
          mini_page_complete: Boolean(row.mini_page_complete),
          mini_page_missing: Array.isArray(row.mini_page_missing)
            ? row.mini_page_missing as PartnerMiniPageMissingField[]
            : [],
          services: Array.isArray(row.services) ? row.services as ServiceType[] : [],
          active: Boolean(row.active),
          latitude: nullableNumber(row.latitude),
          longitude: nullableNumber(row.longitude),
          radius: nullableNumber(row.radius),
          sms_opt_out: Boolean(row.sms_opt_out),
          created_at: nullableString(row.created_at),
          total_leads: toNumber(row.total_leads),
          leads_30d: toNumber(row.leads_30d),
          success_leads: toNumber(row.success_leads),
          last_lead: nullableString(row.last_lead),
          routing_ready: Boolean(row.routing_ready),
          email_ready: Boolean(row.email_ready),
          sms_ready: Boolean(row.sms_ready),
          attention_reasons: Array.isArray(row.attention_reasons)
            ? row.attention_reasons as PartnerAttentionReason[]
            : [],
        }))
      : [],
    summary: Object.fromEntries(
      Object.keys(EMPTY_PARTNER_DIRECTORY_SUMMARY).map((key) => [key, toNumber(summary[key])]),
    ) as unknown as AdminPartnerDirectorySummary,
    service_coverage: Array.isArray(data.service_coverage)
      ? data.service_coverage.map((row: Record<string, unknown>) => ({
          service: String(row.service || "άλλο") as ServiceType,
          active_partners: toNumber(row.active_partners),
        }))
      : [],
    generated_at: String(data.generated_at || ""),
  };
}
